import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import {TargetItemInfo} from 'llm/chat-library/getTargetItem';
import {CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX} from 'llm/chat-library/ui/AIStyleConstants';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import useSmartChipQuery, {ChipItem, SmartChipType} from 'ui-library/chips/useSmartChipQuery';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface TargetItemChipProps {
  targetItem: TargetItemInfo;
}

/**
 * Displays a chip indicator for the target item of a chat. For example, in Compose over Entries,
 * this shows a chip for the item being edited. Includes a "Return" link that navigates back to the
 * target item when the user is viewing a different workspace item or the sidepanel is expanded.
 * The "Return" link is hidden on mobile.
 */
export default function TargetItemChip({targetItem}: TargetItemChipProps): JSX.Element {
  const activeId = useStoreSelector(
    WorkspaceStore,
    useCallback((store: typeof WorkspaceStore) => store.activeId, [])
  );
  const isSidepanelExpanded = useStoreSelector(
    SidepanelStore,
    useCallback((store: typeof SidepanelStore) => store.getIsExpanded(), [])
  );
  const isPhoneWidth = useIsPhoneWidth();

  const {handleClickLink} = useSmartChipQuery<SmartChipType>({
    id: targetItem.itemId,
    getUrlFn: (item: ChipItem<SmartChipType>) => {
      if (item.__typename === 'EntryNode') {
        return item.editUrl;
      }
      // Since there's currently no generic way to get the underlying URL, fail on types
      // that we expect won't come up in practice. This should ideally be moved into a
      // shared function.
      throw new Error(`Unsupported target item type for Return link: ${item.__typename}`);
    },
  });

  const showReturn =
    !isPhoneWidth && (isSidepanelExpanded || activeId == null || activeId !== targetItem.itemId);

  return (
    <div className={css(styles.targetItemOuter)}>
      <div className={css(styles.targetItemGroup)}>
        <BenchlingIcon className={css(styles.editIcon)} icon="pen" />
        <BenchChip className={css(styles.chip)} size={ChipSize.MINI} id={targetItem.itemId} />
        {showReturn && (
          <a className={css(styles.returnLink)} onClick={handleClickLink} role="link" tabIndex={0}>
            Return
          </a>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  targetItemOuter: {
    padding: '8px 12px 0',
  },
  targetItemGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: `1px solid ${Color.GRAY_400}`,
    borderRadius: 4,
    padding: '4px 8px',
    overflow: 'hidden',
  },
  editIcon: {
    color: Color.BLUE_450,
    fontSize: '14px',
    flexShrink: 0,
  },
  chip: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    marginRight: 0,
    minWidth: 0,
    boxSizing: 'border-box',
    minHeight: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    height: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    display: 'inline-flex',
    alignItems: 'center',
    color: Color.BLUE_450,
    ' .chip-icon': {
      color: `${Color.BLUE_450} !important`,
    },
    ' svg.chip-icon': {
      color: `${Color.BLUE_450} !important`,
    },
    ' .chip-text': {
      color: `${Color.BLUE_450} !important`,
    },
    ' .chip-text-secondaryText': {
      color: `${Color.BLUE_450} !important`,
    },
    ' a': {
      color: `${Color.BLUE_450} !important`,
    },
  },
  returnLink: {
    color: Color.BLUE_450,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
});
